import {
  PITCH_SERIES_INTERVAL_MS,
  clampNum,
  mean,
  stabilityScore,
} from '@muzly/voice-engine';
import { midiNoteName } from '@muzly/voice-engine';
import type { PitchSeries } from '@muzly/voice-engine';

import {
  GAUGE_HIGH_MIDI,
  GAUGE_LOW_MIDI,
  gaugeRatio,
} from '../../PitchGaugeTrack';

/**
 * 결과 차트의 좌표 계산. 순수 함수만 있고 React·DOM 의존이 없다 — vitest 대상이다.
 *
 * 그림을 컴포넌트에서 계산하지 않는 이유: 좌표가 틀렸는지는 화면을 봐야만 알 수 있는데,
 * 여기 있으면 숫자로 확인된다. `PitchTraceGraph`가 `traceGeometry`를 쓰는 것과 같은 갈래다.
 *
 * 모든 좌표는 **0..100 view 단위**다(`viewBox="0 0 100 100"`). 컨테이너를 재지 않으므로
 * 계산이 레이아웃에 안 묶인다.
 */

/** 도넛 게이지의 반지름(view 단위). 선 굵기를 뺀 나머지가 안쪽 여백이 된다. */
export const DONUT_RADIUS = 42;
/** 도넛 둘레 — dash 길이를 이 값에서 나눈다. */
export const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * 점수(0..100) → `stroke-dasharray`에 넣을 [칠한 길이, 빈 길이].
 *
 * 각도를 직접 계산해 호(`arc`) 경로를 그리지 않는 이유: 100점일 때 시작점과 끝점이 같은
 * 좌표가 되어 호가 **통째로 사라진다**(SVG 호의 고전적인 함정). dash로 그리면 그 경계가
 * 없다.
 *
 * 범위 밖 점수는 잘라 낸다 — 눈금이 아직 보정 전이라(`uncalibrated`) 101이 들어오면
 * 링이 두 바퀴째를 돌기 시작한다.
 */
export const donutDash = (score: number): [number, number] => {
  const ratio = clampNum(score / 100, 0, 1);
  const filled = DONUT_CIRCUMFERENCE * ratio;
  return [filled, DONUT_CIRCUMFERENCE - filled];
};

/**
 * 건반 막대가 덮는 음역(MIDI). C2(36) ~ C6(84).
 *
 * 시안 §8.4의 x축 눈금이 `C2 C3 C4 C5 C6`이고, 그게 마침 측정 화면 세로 게이지의 창과
 * 같다. **그래서 숫자를 다시 적지 않고 거기서 가져온다** — 두 화면이 같은 목소리를 다른
 * 폭으로 그리면 진단 중에 본 위치와 결과에서 본 위치가 어긋나는데, 상수를 복사해 두면
 * 한쪽만 옮겨도 아무도 안 깨진다.
 */
export const KEYBOARD_LOW_MIDI = GAUGE_LOW_MIDI;
export const KEYBOARD_HIGH_MIDI = GAUGE_HIGH_MIDI;

/**
 * MIDI → 막대 위 가로 위치(0..1, 0 = 왼쪽 = 저음). 창 밖은 끝에 붙는다.
 *
 * 세로 게이지의 `gaugeRatio`와 **같은 함수**다(0이 저음, 1이 고음). 축이 가로냐 세로냐는
 * 그리는 쪽이 정한다.
 */
export const keyboardRatio = gaugeRatio;

/**
 * 음역 구간 → 막대에서 칠할 [시작 %, 폭 %].
 *
 * 폭이 0이 되는 경우(최저음 = 최고음, 한 음만 낸 회차)에도 **보이는 폭**을 준다. 0폭
 * 사각형은 아무것도 안 그려져서, 한 음만 낸 회차가 "아무것도 못 냈다"로 보인다.
 */
export const MIN_BAND_WIDTH_PCT = 1.5;

export const keyboardBand = (
  lowMidi: number,
  highMidi: number,
): { x: number; width: number } => {
  const from = keyboardRatio(Math.min(lowMidi, highMidi)) * 100;
  const to = keyboardRatio(Math.max(lowMidi, highMidi)) * 100;
  const width = Math.max(to - from, MIN_BAND_WIDTH_PCT);
  // 오른쪽 끝에서 최소 폭을 주면 막대 밖으로 넘친다 — 그만큼 왼쪽으로 당긴다.
  return { x: Math.min(from, 100 - width), width };
};

/**
 * 눈금 위치 — 시안의 `C2 C3 C4 C5 C6`.
 *
 * 라벨을 손으로 만들지 않는다(`C${octave}` 꼴로 짜면 C가 아닌 눈금을 하나 넣는 순간
 * `D2`를 `C2`로 적으면서도 빌드는 통과한다). 음이름은 엔진이 이름 짓는다.
 */
export const KEYBOARD_TICKS = [36, 48, 60, 72, 84].map((midi) => ({
  midi,
  label: midiNoteName(midi),
  x: keyboardRatio(midi) * 100,
}));

/**
 * 호흡 라인 차트의 y축 범위(점수). 시안 §8.7이 `20~100`으로 그린다.
 *
 * 0에서 시작하지 않는 이유: 실측 점수가 30~95에 몰려 있어 0부터 그리면 세 점이 위쪽
 * 3분의 1에 붙어 구간별 차이가 안 보인다. 대신 바닥이 0이 아님을 축 라벨이 말한다.
 */
export const BREATH_Y_MIN = 20;
export const BREATH_Y_MAX = 100;

/** 점수 → 라인 차트의 세로 위치(0 = 위 = 100점). 범위 밖은 끝에 붙는다. */
export const breathY = (score: number): number => {
  const span = BREATH_Y_MAX - BREATH_Y_MIN;
  const ratio = clampNum((score - BREATH_Y_MIN) / span, 0, 1);
  return (1 - ratio) * 100;
};

/**
 * 구간 점수들 → 꺾은선의 자리. x는 **구간의 가운데 시각**이다(경계가 아니라).
 *
 * 점수는 구간 전체를 한 값으로 요약한 것이라, 경계에 찍으면 그 순간에 그 값이었다는
 * 뜻이 된다. 가운데에 찍으면 "이 구간이 이랬다"로 읽힌다.
 *
 * 값이 없는 구간도 **칸은 남기고 `y`만 null**이다. 배열에서 빼 버리면 그 구멍 위로 선이
 * 곧장 이어져 재지도 않은 구간에 값이 있었던 것처럼 보인다 — 0으로 찍는 것과 같은 거짓말을
 * 선분이 대신 한다. 선을 끊는 일은 `linePath`가 한다.
 */
export const breathPoints = (
  segments: readonly { id: string; fromSec: number; toSec: number }[],
  scores: readonly { id: string; score: number }[],
  spanSec: number,
): { id: string; x: number; y: number | null; score: number | null }[] =>
  segments.map((segment) => {
    const found = scores.find((entry) => entry.id === segment.id);
    const midSec = (segment.fromSec + segment.toSec) / 2;
    return {
      id: segment.id,
      x: (midSec / spanSec) * 100,
      y: found ? breathY(found.score) : null,
      score: found ? found.score : null,
    };
  });

/**
 * 이동 안정도 창의 반폭(초). 1초 창이면 6Hz 떨림 여섯 주기가 들어가 떨림 한 번에 곡선이
 * 출렁이지 않고, 구간(3~4초)보다 충분히 짧아 "어디서 흔들렸나"가 보인다(미보정).
 */
const BREATH_CURVE_HALF_WINDOW_SEC = 0.5;
/**
 * 창 안에 이만큼은 값이 있어야 점수를 낸다(창 칸 수 대비 비율). 숨 끝·시작처럼 몇 칸만
 * 걸친 자리의 흩어짐은 대부분 운이라 — 구간 점수가 16프레임 미만을 버리는 것과 같은 이유.
 */
const BREATH_CURVE_MIN_FILL = 0.4;

/**
 * 음정 곡선 → 호흡 안정도의 **실측 곡선**(TestFlight #56: "3포인트 직선 → 실측 곡선").
 *
 * 세로축은 구간 점수와 같은 **안정도 점수**다 — 각 시점 둘레 1초 창의 흔들림을 구간 점수와
 * 같은 자(`stabilityScore` · 가장 가까운 반음 기준으로 접은 cents)로 잰다. 음정 자체를
 * 그리면 세로축이 cents가 되어 구간 점과 한 차트에 못 선다. 곡선 값은 테이크 중앙값 기준이라
 * 접는 경계가 구간 점수와 조금 다를 수 있다(중앙값이 반음 사이에 있을 때).
 *
 * 숨 자리(`null`)는 선을 끊는다. x는 `spanSec`(차트 폭) 대비 백분율, 그 뒤는 그리지 않는다.
 */
export const breathCurve = (
  series: PitchSeries | undefined,
  spanSec: number,
): { x: number; y: number | null }[] => {
  if (!series || spanSec <= 0) {
    return [];
  }
  const { intervalMs, values } = series;
  const half = Math.round((BREATH_CURVE_HALF_WINDOW_SEC * 1000) / intervalMs);
  const need = Math.max(2, Math.ceil((2 * half + 1) * BREATH_CURVE_MIN_FILL));
  const last = Math.min(
    values.length - 1,
    Math.floor((spanSec * 1000) / intervalMs),
  );
  const points: { x: number; y: number | null }[] = [];
  for (let i = 0; i <= last; i++) {
    const x = ((i * intervalMs) / 1000 / spanSec) * 100;
    // 구간 점수와 같은 자로 잰다 — 가장 가까운 반음까지의 거리(±50으로 접힌 cents,
    // `centsOffNearestSemitone`). 테이크 중앙값 기준 cents를 그대로 재면 떨림이 큰 구간에서
    // 곡선이 같은 구간의 점보다 30점 가까이 낮게 선다(QA 실측: 곡선 53 · 점 81).
    const window = values
      .slice(Math.max(0, i - half), i + half + 1)
      .filter((v): v is number => v !== null)
      .map((c) => c - 100 * Math.round(c / 100));
    points.push({
      x,
      y:
        values[i] === null || window.length < need
          ? null
          : breathY(stabilityScore(window)),
    });
  }
  return points;
};

/**
 * 점들 → SVG `path`의 `d`. 점이 하나면 선이 아니라 **점 하나**로 남는다.
 *
 * `y`가 null인 칸에서 펜을 든다(다음 점은 `L`이 아니라 `M`으로 시작한다) — 홈 추세 차트가
 * 결측일을 끊는 방식과 같다(`features/home/TrendChart.buildPaths`). 구멍 위로 선을 이으면
 * 그 구간에 값이 있었다고 말하는 셈이다.
 */
export const linePath = (points: { x: number; y: number | null }[]): string => {
  let d = '';
  let penDown = false;
  for (const point of points) {
    if (point.y === null) {
      penDown = false;
      continue;
    }
    d += `${penDown ? 'L' : 'M'}${point.x} ${point.y} `;
    penDown = true;
  }
  return d.trim();
};

/**
 * 점들 → **매끄러운** SVG `path` — 표본을 모두 지나는 Catmull-Rom 곡선을 3차 베지어로 옮긴다.
 *
 * 떨림 곡선은 50ms 칸이라 5~6Hz 한 주기가 서너 칸이다. `linePath`처럼 직선으로 이으면 톱니로
 * 보여 떨림의 외형을 말하지 못한다(QA). 곡선은 표본 값을 그대로 지나고 사이만 부드럽게 채운다.
 * 빈 칸(`null`)에서는 `linePath`와 같이 펜을 든다.
 */
export const smoothPath = (
  points: { x: number; y: number | null }[],
): string => {
  const runs: { x: number; y: number }[][] = [];
  points.forEach((p, i) => {
    if (p.y === null) {
      return;
    }
    if (i === 0 || points[i - 1].y === null) {
      runs.push([]);
    }
    runs[runs.length - 1].push({ x: p.x, y: p.y });
  });
  const f = (n: number) => Number(n.toFixed(2));
  return runs
    .map((run) => {
      let d = `M${f(run[0].x)} ${f(run[0].y)}`;
      for (let i = 0; i < run.length - 1; i++) {
        const p0 = run[Math.max(0, i - 1)];
        const p1 = run[i];
        const p2 = run[i + 1];
        const p3 = run[Math.min(run.length - 1, i + 2)];
        const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
        const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
        d += ` C${f(c1.x)} ${f(c1.y)} ${f(c2.x)} ${f(c2.y)} ${f(p2.x)} ${f(p2.y)}`;
      }
      return d;
    })
    .join(' ');
};

/**
 * 떨림 파형의 표본 — **잰 값으로 그린 모식도**다(시안 §8.6).
 *
 * 음정 곡선(`pitchSeries`)이 없는 회차의 폴백이다 — 서버에서 복원했거나 그 필드 이전에
 * 끝낸 회차는 속도(Hz)·폭(cents)·규칙성만 남는다. 그래서 이 곡선은 녹음의 그림이 아니라
 * **두 숫자를 눈으로 옮긴 것**이고, 화면 문구가 그렇게 말한다(`vibrato.waveNote`). 곡선이
 * 있으면 `vibratoTrace`가 대신한다.
 *
 * y는 cents를 `spanCents` 창에 맞춘 0..100이다(50이 기준선).
 */
export const vibratoWave = (
  rateHz: number,
  depthCents: number,
  spanSec: number,
  spanCents: number,
  samples = 120,
): { x: number; y: number }[] =>
  Array.from({ length: samples + 1 }, (_, i) => {
    const sec = (i / samples) * spanSec;
    const cents = Math.sin(2 * Math.PI * rateHz * sec) * depthCents;
    const ratio = clampNum(cents / spanCents, -1, 1);
    return { x: (i / samples) * 100, y: 50 - ratio * 50 };
  });

/**
 * 흐름을 빼는 이동 평균의 폭(초). 떨림(3~10Hz, 한 주기 100~333ms)보다 길어 떨림은 남기고,
 * 지속음이 천천히 처지거나 뜨는 흐름은 따라가 빠진다 — 떨림 분석(`analyzeVibrato`)의 첫
 * 패스 창과 같은 값이다.
 */
const VIBRATO_TRACE_DETREND_SEC = 0.5;
/** 창 안에 이만큼(초)은 소리가 있어야 그린다. 그보다 짧으면 주기 몇 개로 외형을 말할 수 없다. */
const VIBRATO_TRACE_MIN_SEC = 1;

/**
 * 그린 폭을 잰 폭에 맞추는 배율의 한계. 칸 평균이 깎는 만큼(실측 20~55%)은 되돌리되,
 * 곡선이 잰 값과 너무 다르면(창에 떨림이 거의 없거나 소음이 섞였다) 배율로 우기지 않는다.
 */
const VIBRATO_TRACE_SCALE = { min: 0.5, max: 3 };

/**
 * 그린 곡선의 반폭 — 기준선 위 봉우리 평균과 아래 골 평균 사이의 절반. 떨림 분석의 폭(주기마다
 * 봉우리 간 거리의 절반)과 같은 양이고, **눈에 보이는** 봉우리로 잰다: 50ms 칸은 5Hz 한 주기에
 * 네 칸이라 봉우리 칸이 실제 꼭지보다 낮게 찍힌다 — 맞춰야 할 것은 그 그려진 봉우리다.
 * 봉우리·골이 없으면(흔들림이 거의 없다) 0.
 */
const drawnHalfSwing = (wobble: (number | null)[]): number => {
  const peaks: number[] = [];
  const troughs: number[] = [];
  for (let k = 1; k < wobble.length - 1; k++) {
    const [a, v, b] = [wobble[k - 1], wobble[k], wobble[k + 1]];
    if (a === null || v === null || b === null) {
      continue;
    }
    if (v > 0 && v >= a && v > b) {
      peaks.push(v);
    } else if (v < 0 && v <= a && v < b) {
      troughs.push(v);
    }
  }
  return peaks.length === 0 || troughs.length === 0
    ? 0
    : (mean(peaks) - mean(troughs)) / 2;
};

/**
 * 음정 곡선 → **그 회차의 떨림** `spanSec`초(TestFlight #57: "모식도 대신 실제 외형").
 *
 * 소리가 가장 많이 담긴 창을 고르고(동률이면 테이크 가운데 쪽 — 맨 앞은 음을 긁어 올리는
 * 자리다), 음 전체의 흐름을 뺀 흔들림만 그린다 — 참고 구간 띠(±50~120 cents)가 기준선
 * 둘레의 흔들림을 재는 자라서다.
 *
 * **폭은 잰 값(`depthCents`)에 맞춘다.** 곡선은 50ms 칸 평균이라 봉우리가 깎인다(±60 떨림이
 * ±37~46으로 — 05 리뷰 실측). 모양·속도·폭의 시간 변화는 그 테이크 그대로 두고, 전체 폭만
 * 프레임 해상도로 잰 비브라토 분석의 값으로 되돌린다. 그래야 탭의 "폭 ±60"과 띠와 곡선이
 * 같은 말을 한다.
 *
 * y는 `vibratoWave`와 같은 0..100(50 = 기준선), 숨 자리는 null. null이면 호출부가 모식도로
 * 돌아간다 — 곡선이 없거나, 소리가 `VIBRATO_TRACE_MIN_SEC`에 못 미치거나, 칸이 기본(50ms)보다
 * 넓어(20초 넘는 테이크) 한 주기에 몇 칸 안 남을 때.
 */
export const vibratoTrace = (
  series: PitchSeries | undefined,
  depthCents: number,
  spanSec: number,
  spanCents: number,
): { x: number; y: number | null }[] | null => {
  if (!series || series.intervalMs > PITCH_SERIES_INTERVAL_MS) {
    return null;
  }
  const { intervalMs, values } = series;
  const slots = Math.max(1, Math.round((spanSec * 1000) / intervalMs));
  const need = Math.ceil((VIBRATO_TRACE_MIN_SEC * 1000) / intervalMs);
  const half = Math.max(
    1,
    Math.round((VIBRATO_TRACE_DETREND_SEC * 1000) / intervalMs / 2),
  );

  const centre = Math.max(0, (values.length - slots - 1) / 2);
  let start = 0;
  let best = -1;
  for (let s = 0; s === 0 || s + slots < values.length; s++) {
    const count = values
      .slice(s, s + slots + 1)
      .filter((v) => v !== null).length;
    if (
      count > best ||
      (count === best && Math.abs(s - centre) < Math.abs(start - centre))
    ) {
      best = count;
      start = s;
    }
  }
  if (best < need) {
    return null;
  }

  const window = values.slice(start, start + slots + 1);
  const wobble = window.map((value, k) => {
    if (value === null) {
      return null;
    }
    const i = start + k;
    const around = values
      .slice(Math.max(0, i - half), i + half + 1)
      .filter((v): v is number => v !== null);
    return value - mean(around);
  });
  const drawnHalf = drawnHalfSwing(wobble);
  const scale =
    drawnHalf > 0
      ? clampNum(
          depthCents / drawnHalf,
          VIBRATO_TRACE_SCALE.min,
          VIBRATO_TRACE_SCALE.max,
        )
      : 1;

  return wobble.map((v, k) => ({
    x: (k / slots) * 100,
    y: v === null ? null : 50 - clampNum((v * scale) / spanCents, -1, 1) * 50,
  }));
};
